import React, { useState, useLayoutEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import PropTypes from 'prop-types';
import Card from "./Card";
import DashboardCardSkeleton from "./DashboardCardSkeleton";
import { checkMqttCard } from "../../utils/cardUtils";
import { useAuth0 } from '@auth0/auth0-react';
import { useCards, useActions, useDeleteCard, useDeleteAction, setUserValue } from "../../utils/reactQueryHooks";
import "../../styles/dashboardCards.css"; // Import dashboard-specific card styling 
import "../../styles/actions.css";
import ChatBot from "../common/ChatBot";
import Action from "../actions/Action";
import NotificationsConsole from "../common/NotificationsConsole";
import MqttMessageViewer from "../common/MqttMessageViewer";
import CarePlanActivity from "../common/CarePlanActivity";
import CareEventsModal from "../common/CareEventsModal";
import DashboardTour from "../common/DashboardTour";
import DeleteConfirmModal from "../common/DeleteConfirmModal";
import { useChatUIPersistence } from "../common/ChatUIPersistenceContext";
import { usePairingMismatches, usePairingRepair } from "../actions/ActuatorPresence";
import PairingCheck from "../actions/PairingCheck";
import chatIcon from "../../assets/images/chat.png";
import "../../styles/chatbot.css";

const normalizeMacAddress = (mac) =>
    typeof mac === "string" ? mac.trim().replace(/;/g, ":").toUpperCase() : "";

const formatActuatorTypeLabel = (type) => {
    if (!type) return "Unknown";
    if (type === "irrigation2") return "Irrigation 2";
    return type.charAt(0).toUpperCase() + type.slice(1);
};

const ACTUATOR_FILTER_DEFAULTS = {
    actuatorType: "",
    sensorMac: "",
    status: "",
    searchText: "",
    groupBy: "sensor",
};

function Dashboard({ tourRunning, onTourComplete, onTourSkip }) {
    const { user, isAuthenticated } = useAuth0();
    const { isChatModalOpen, setIsChatModalOpen } = useChatUIPersistence();
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [mqttViewerOpen, setMqttViewerOpen] = useState(false);
    const [carePlanActivityOpen, setCarePlanActivityOpen] = useState(false);
    const [careEventsOpen, setCareEventsOpen] = useState(false);
    const [sensorDeleteMac, setSensorDeleteMac] = useState(null);
    const [actionDeleteMac, setActionDeleteMac] = useState(null);
    const [actuatorFilters, setActuatorFilters] = useState(ACTUATOR_FILTER_DEFAULTS);
    
    // Get userKey for React Query
    const userKey = useMemo(() => {
        if (isAuthenticated && user) {
            const username = user.nickname || user.email;
        
            return username;
        }
        const storedUserKey = localStorage.getItem('userVal') ? JSON.parse(localStorage.getItem('userVal')).data : null;
    
        return storedUserKey;
    }, [isAuthenticated, user]);
    
    // Sync userKey to localStorage before paint so fetchCards and other hooks agree
    useLayoutEffect(() => {
        if (isAuthenticated && user && userKey) {
            setUserValue(userKey);
        }
    }, [isAuthenticated, user, userKey]);
    
    // Use React Query for cards data; respect staleTime (no forced refetch on every mount)
    const {
        data: cardsData = [],
        isPending: isCardsPending,
        isFetching: isCardsFetching,
        dataUpdatedAt,
    } = useCards({
        userKey,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        enabled: !!userKey,
    });

    const deleteCardMutation = useDeleteCard();
    const deleteActionMutation = useDeleteAction();
    const { data: actions = [], isPending: isActionsPending } = useActions();
    const pairingMismatches = usePairingMismatches();
    const { repairPairing, repairStatus } = usePairingRepair();
    
    // Set cards from React Query data
    const cards = useMemo(() => cardsData || [], [cardsData]);

    const pendingSensorDelete = useMemo(
        () => cards.find((c) => c.macAddress === sensorDeleteMac) ?? null,
        [cards, sensorDeleteMac]
    );
    const pendingActionDelete = useMemo(
        () => actions.find((action) => action.macAddress === actionDeleteMac) ?? null,
        [actions, actionDeleteMac]
    );
    const pendingSensorPairedActions = useMemo(
        () =>
            actions.filter(
                (action) =>
                    normalizeMacAddress(action.sensor) ===
                    normalizeMacAddress(pendingSensorDelete?.macAddress)
            ),
        [actions, pendingSensorDelete]
    );

    const cardByMac = useMemo(
        () => new Map(cards.map((card) => [normalizeMacAddress(card.macAddress), card])),
        [cards]
    );

    const actuatorFilterOptions = useMemo(() => {
        const types = new Set();
        const statuses = new Set();
        const sensors = new Map();

        for (const action of actions) {
            if (action.actuatorType) types.add(action.actuatorType);
            if (action.status) statuses.add(action.status);

            const sensorKey = normalizeMacAddress(action.sensor);
            if (!sensorKey) continue;

            const card = cardByMac.get(sensorKey);
            sensors.set(sensorKey, {
                mac: action.sensor,
                label: card?.sensorDescription || action.sensor,
            });
        }

        return {
            actuatorTypes: Array.from(types).sort((a, b) =>
                formatActuatorTypeLabel(a).localeCompare(formatActuatorTypeLabel(b))
            ),
            statuses: Array.from(statuses).sort(),
            sensors: Array.from(sensors.values()).sort((a, b) =>
                a.label.localeCompare(b.label)
            ),
        };
    }, [actions, cardByMac]);

    const filteredActions = useMemo(() => {
        const search = actuatorFilters.searchText.trim().toLowerCase();

        return actions.filter((action) => {
            if (actuatorFilters.actuatorType && action.actuatorType !== actuatorFilters.actuatorType) {
                return false;
            }
            if (
                actuatorFilters.sensorMac &&
                normalizeMacAddress(action.sensor) !== normalizeMacAddress(actuatorFilters.sensorMac)
            ) {
                return false;
            }
            if (actuatorFilters.status && action.status !== actuatorFilters.status) {
                return false;
            }
            if (search) {
                const card = cardByMac.get(normalizeMacAddress(action.sensor));
                const haystack = [
                    action.actuatorDescription,
                    action.macAddress,
                    action.actuatorType,
                    action.sensor,
                    card?.sensorDescription,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                if (!haystack.includes(search)) return false;
            }
            return true;
        });
    }, [actions, actuatorFilters, cardByMac]);

    const groupedActuators = useMemo(() => {
        if (actuatorFilters.groupBy === "type") {
            const groups = new Map();

            for (const action of filteredActions) {
                const type = action.actuatorType || "unknown";
                if (!groups.has(type)) {
                    groups.set(type, {
                        groupKey: type,
                        groupLabel: formatActuatorTypeLabel(type),
                        groupSubLabel: null,
                        actions: [],
                    });
                }
                groups.get(type).actions.push(action);
            }

            return Array.from(groups.values()).sort((a, b) =>
                a.groupLabel.localeCompare(b.groupLabel)
            );
        }

        const groups = new Map();

        for (const action of filteredActions) {
            const sensorMac = normalizeMacAddress(action.sensor);
            if (!groups.has(sensorMac)) {
                const card = cardByMac.get(sensorMac);
                groups.set(sensorMac, {
                    groupKey: sensorMac || action.sensor || action.macAddress,
                    groupLabel: card?.sensorDescription || action.sensor || "Unknown sensor",
                    groupSubLabel: action.sensor || null,
                    actions: [],
                });
            }
            groups.get(sensorMac).actions.push(action);
        }

        return Array.from(groups.values()).sort((a, b) => {
            const aIndex = cards.findIndex(
                (card) => normalizeMacAddress(card.macAddress) === normalizeMacAddress(a.groupSubLabel)
            );
            const bIndex = cards.findIndex(
                (card) => normalizeMacAddress(card.macAddress) === normalizeMacAddress(b.groupSubLabel)
            );
            if (aIndex === -1 && bIndex === -1) {
                return a.groupLabel.localeCompare(b.groupLabel);
            }
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });
    }, [filteredActions, actuatorFilters.groupBy, cardByMac, cards]);

    const hasActiveActuatorFilters = useMemo(
        () =>
            actuatorFilters.actuatorType !== "" ||
            actuatorFilters.sensorMac !== "" ||
            actuatorFilters.status !== "" ||
            actuatorFilters.searchText.trim() !== "" ||
            actuatorFilters.groupBy !== ACTUATOR_FILTER_DEFAULTS.groupBy,
        [actuatorFilters]
    );

    const updateActuatorFilter = (key, value) => {
        setActuatorFilters((current) => ({ ...current, [key]: value }));
    };

    const clearActuatorFilters = () => {
        setActuatorFilters(ACTUATOR_FILTER_DEFAULTS);
    };
    
    // MQTT addresses are now derived directly from cards in useMQTTSensorData
    // No need for separate initialization logic here
    
    // Use memoized value instead of state to prevent infinite updates
    const lastUpdated = useMemo(() => {
        return dataUpdatedAt ? new Date(dataUpdatedAt) : new Date();
    }, [dataUpdatedAt]);

    const handleRequestDeleteSensorCard = (macAddress) => {
        setSensorDeleteMac(macAddress);
    };

    const handleCancelDeleteSensorCard = () => {
        setSensorDeleteMac(null);
    };

    const handleConfirmDeleteSensorCard = () => {
        if (!sensorDeleteMac) return;
        const macAddress = sensorDeleteMac;
        deleteCardMutation.mutate(macAddress, {
            onSuccess: () => {
                console.log(`Card with MAC address ${macAddress} successfully deleted`);
                setSensorDeleteMac(null);
            },
            onError: (error) => {
                console.error(`Error deleting card: ${error.message}`);
            }
        });
    };

    const handleConfirmDeleteAction = () => {
        if (!actionDeleteMac) return;
        const macAddress = actionDeleteMac;
        deleteActionMutation.mutate(macAddress, {
            onSuccess: () => setActionDeleteMac(null),
            onError: (error) => {
                console.error(`Error deleting action: ${error.message}`);
            }
        });
    };

    // ChatBot functions
    const openChatBot = () => setIsChatModalOpen(prev => !prev);
    const closeChatBot = () => setIsChatModalOpen(false);
    // Notifications Console functions
    const toggleNotifications = () => setNotificationsOpen(!notificationsOpen);
    
    // MQTT Message Viewer functions
    const toggleMqttViewer = () => setMqttViewerOpen(!mqttViewerOpen);
    
    // Care Plan Activity functions
    const toggleCarePlanActivity = () => setCarePlanActivityOpen(!carePlanActivityOpen);
    
    // Care Events Modal functions
    const toggleCareEvents = () => setCareEventsOpen(!careEventsOpen);
    
    const showCardSkeletons = (isCardsPending && cards.length === 0) || !userKey;
    const showBackgroundRefresh = isCardsFetching && !showCardSkeletons;

    return (
        <>
         <div className="dashboard-container">
            {/* Last updated timestamp - moved up to fill space */}
            <div className="dashboard-header-container" data-tourid="dashboard-header">
                {showBackgroundRefresh && (
                    <div className="dashboard-loading-container">
                        <div className="dashboard-loading-indicator">
                            <div className="loading-spinner"></div>
                        </div>
                        <p className="dashboard-loading-text">Fetching new data...</p>
                    </div>
                )}
                {showCardSkeletons && (
                    <p className="dashboard-header-skeleton-text">Loading your dashboard...</p>
                )}
                {!showBackgroundRefresh && !showCardSkeletons && (
                    <div className="dashboard-last-updated">
                        <p className="dashboard-last-updated-text">
                            Last updated: {" "}
                            {lastUpdated.toLocaleDateString(
                                'en-US', {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true
                                }
                            )}
                        </p>
                    </div>
                )}
            </div>

            {/*Render all of the cards we currently have*/}
            <div className="dashboard-cards-container">
                {showCardSkeletons && <DashboardCardSkeleton count={3} />}
                {!showCardSkeletons && cards.map((card, index) => {
                    // Add tour ID to first sensor card and first action card if they exist
                    const isFirstSensorCard = index === 0 && !checkMqttCard(card?.macAddress);
                    const isFirstMqttCard = index === 0 && checkMqttCard(card?.macAddress);
                    const tourId = isFirstSensorCard || isFirstMqttCard ? 'sensor-card' : null;
                    
                    return (
                        <div key={card?.macAddress || crypto.randomUUID()} data-tourid={tourId}>
                            <Card 
                                sensor={card}
                                onDelete={handleRequestDeleteSensorCard}
                            />
                        </div>
                    );
                })}
            </div>

            <section className="dashboard-actuators" aria-labelledby="dashboard-actuators-title">
                <div className="dashboard-actuators-header">
                    <h2 id="dashboard-actuators-title" className="section-title">Connected Actuators</h2>
                    <div className="dashboard-actuators-header-meta">
                        <Link className="dashboard-history-link" to="/devices/history">Device history</Link>
                        {!isActionsPending && actions.length > 0 && (
                            <span className="dashboard-actuators-count">
                                {filteredActions.length} of {actions.length}
                            </span>
                        )}
                    </div>
                </div>

                {!isActionsPending && actions.length > 0 && (
                    <div className="dashboard-actuator-filters">
                        <div className="dashboard-actuator-filters-row">
                            <div className="dashboard-actuator-filter-group">
                                <label htmlFor="actuator-filter-group-by">Group by</label>
                                <select
                                    id="actuator-filter-group-by"
                                    className="dashboard-actuator-filter-select"
                                    value={actuatorFilters.groupBy}
                                    onChange={(e) => updateActuatorFilter("groupBy", e.target.value)}
                                >
                                    <option value="sensor">Sensor zone</option>
                                    <option value="type">Actuator type</option>
                                </select>
                            </div>

                            <div className="dashboard-actuator-filter-group">
                                <label htmlFor="actuator-filter-type">Type</label>
                                <select
                                    id="actuator-filter-type"
                                    className="dashboard-actuator-filter-select"
                                    value={actuatorFilters.actuatorType}
                                    onChange={(e) => updateActuatorFilter("actuatorType", e.target.value)}
                                >
                                    <option value="">All types</option>
                                    {actuatorFilterOptions.actuatorTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {formatActuatorTypeLabel(type)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="dashboard-actuator-filter-group">
                                <label htmlFor="actuator-filter-sensor">Sensor zone</label>
                                <select
                                    id="actuator-filter-sensor"
                                    className="dashboard-actuator-filter-select"
                                    value={actuatorFilters.sensorMac}
                                    onChange={(e) => updateActuatorFilter("sensorMac", e.target.value)}
                                >
                                    <option value="">All zones</option>
                                    {actuatorFilterOptions.sensors.map((sensor) => (
                                        <option key={sensor.mac} value={sensor.mac}>
                                            {sensor.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="dashboard-actuator-filter-group">
                                <label htmlFor="actuator-filter-status">Status</label>
                                <select
                                    id="actuator-filter-status"
                                    className="dashboard-actuator-filter-select"
                                    value={actuatorFilters.status}
                                    onChange={(e) => updateActuatorFilter("status", e.target.value)}
                                >
                                    <option value="">All statuses</option>
                                    {actuatorFilterOptions.statuses.map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="dashboard-actuator-filter-group dashboard-actuator-filter-group-search">
                                <label htmlFor="actuator-filter-search">Search</label>
                                <input
                                    id="actuator-filter-search"
                                    type="search"
                                    className="dashboard-actuator-filter-input"
                                    placeholder="Name or MAC"
                                    value={actuatorFilters.searchText}
                                    onChange={(e) => updateActuatorFilter("searchText", e.target.value)}
                                />
                            </div>
                        </div>

                        {hasActiveActuatorFilters && (
                            <div className="dashboard-actuator-filters-actions">
                                <button
                                    type="button"
                                    className="dashboard-actuator-clear-filters"
                                    onClick={clearActuatorFilters}
                                >
                                    Clear filters
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {isActionsPending ? (
                    <p>Loading actuators...</p>
                ) : actions.length > 0 ? (
                    groupedActuators.length > 0 ? (
                        <div className="dashboard-actuator-groups">
                            {groupedActuators.map((group) => (
                                <section
                                    key={group.groupKey}
                                    className="dashboard-actuator-group"
                                    aria-label={`Actuators for ${group.groupLabel}`}
                                >
                                    <div className="dashboard-actuator-group-header">
                                        <h3 className="dashboard-actuator-group-title">{group.groupLabel}</h3>
                                        {group.groupSubLabel && (
                                            <p
                                                className="dashboard-actuator-group-mac"
                                                title={group.groupSubLabel}
                                            >
                                                {group.groupSubLabel}
                                            </p>
                                        )}
                                        <p className="dashboard-actuator-group-count">
                                            {group.actions.length}{" "}
                                            {group.actions.length === 1 ? "actuator" : "actuators"}
                                        </p>
                                    </div>
                                    <PairingCheck
                                        mismatches={pairingMismatches.filter((item) =>
                                            group.actions.some(
                                                (action) =>
                                                    (action.macAddress || "").trim().replace(/;/g, ":").toUpperCase()
                                                    === item.macAddress
                                            )
                                        )}
                                        repairStatus={repairStatus}
                                        onRepair={repairPairing}
                                    />
                                    <div className="sensor-actions">
                                        {group.actions.map((action) => (
                                            <Action
                                                key={action.macAddress}
                                                action={action}
                                                onDelete={setActionDeleteMac}
                                                showMacAddress
                                            />
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    ) : (
                        <p>No actuators match your filters.</p>
                    )
                ) : (
                    <p>No actuators are registered to this account.</p>
                )}
            </section>

            <div className="dashboard-footer">
                {/*FIXME: If anything need be added here in the future.*/}
            </div>
            
            {/* Notifications Console */}
            <div data-tourid="notifications-button">
                <NotificationsConsole 
                    isOpen={notificationsOpen}
                    onToggle={toggleNotifications}
                    hideButton={mqttViewerOpen || carePlanActivityOpen || careEventsOpen}
                />
            </div>
            
            {/* MQTT Message Viewer */}
            <MqttMessageViewer 
                isOpen={mqttViewerOpen}
                onToggle={toggleMqttViewer}
                hideButton={notificationsOpen || carePlanActivityOpen || careEventsOpen}
            />
            
            {/* Care Plan Activity */}
            <CarePlanActivity 
                isOpen={carePlanActivityOpen}
                onClose={toggleCarePlanActivity}
                hideButton={notificationsOpen || mqttViewerOpen || careEventsOpen}
            />
            
            {/* Care Events Modal */}
            <CareEventsModal 
                isOpen={careEventsOpen}
                onToggle={toggleCareEvents}
                hideButton={notificationsOpen || mqttViewerOpen || carePlanActivityOpen}
            />
            
            {/* ChatBot Button */}
            <button 
                className="chatbot-toggle-button" 
                onClick={openChatBot}
                aria-label="Open Chat Assistant"
                title="Chat with Canopy AI Assistant"
                data-tourid="chatbot-button"
            >
                <img src={chatIcon} alt="Chat" className="chatbot-icon" />
            </button>
            
            {/* ChatBot Modal */}
            {isChatModalOpen && (
                <ChatBot 
                    isModal={true}
                    closeChatModal={closeChatBot}
                    modalPosition="bottom-right"
                    metaData={{
                        macAddress: "", // Can be populated with selected card's MAC if needed
                        timeRange: "24h" // Default time range
                    }}
                />
            )}
            
            {/* Dashboard Tour */}
            <DashboardTour 
                run={tourRunning}
                onComplete={onTourComplete}
                onSkip={onTourSkip}
            />

            <DeleteConfirmModal
                isOpen={Boolean(sensorDeleteMac)}
                onClose={handleCancelDeleteSensorCard}
                onConfirm={handleConfirmDeleteSensorCard}
                title="Disconnect sensor?"
                message={
                    pendingSensorDelete?.sensorDescription
                        ? `Disconnect "${pendingSensorDelete.sensorDescription}" from your dashboard?`
                        : 'Disconnect this sensor from your dashboard?'
                }
                warningText={
                    pendingSensorPairedActions.length
                        ? `This will also disconnect ${pendingSensorPairedActions.length} paired actuator${pendingSensorPairedActions.length === 1 ? '' : 's'}. You can reconnect them later from Device History.`
                        : 'You can reconnect this sensor later from Device History, as long as the MAC is not claimed by another account.'
                }
                confirmLabel="Disconnect"
                isLoading={deleteCardMutation.isPending}
            />

            <DeleteConfirmModal
                isOpen={Boolean(actionDeleteMac)}
                onClose={() => setActionDeleteMac(null)}
                onConfirm={handleConfirmDeleteAction}
                title="Disconnect actuator?"
                message={
                    pendingActionDelete?.actuatorDescription
                        ? `Disconnect "${pendingActionDelete.actuatorDescription}" from your dashboard?`
                        : 'Disconnect this actuator from your dashboard?'
                }
                warningText={
                    pendingActionDelete?.sensor
                        ? `Last paired zone will be saved. Pause any active action plans first. You can reconnect it later from Device History.`
                        : 'You can reconnect this actuator later from Device History, as long as the MAC is not claimed by another account.'
                }
                confirmLabel="Disconnect"
                isLoading={deleteActionMutation.isPending}
            />
        </div>
        

    </>
    )
}

Dashboard.propTypes = {
    tourRunning: PropTypes.bool.isRequired,
    onTourComplete: PropTypes.func.isRequired,
    onTourSkip: PropTypes.func.isRequired,
};

// Export a memoized version of the Dashboard to prevent unnecessary re-renders
export default React.memo(Dashboard);